import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import Authentication from 'utils/authentication';
import request from 'library/@e5p-request';

import cx from 'classnames';

import Page from 'containers/layouts/page';
import Content from 'containers/layouts/content';
import Main from 'containers/layouts/main';

import Card from 'components/card';
import Widget from 'components/widget';
import Input from 'components/input';
import Button from 'components/button';

import Loader from 'components/loader';

import Logo from 'components/logo';

import SelectLanguage from 'containers/elements/@commons/select-language';
import SelectTheme from 'containers/elements/@commons/select-theme';

import Text from 'components/text';

import { getSsoErrorMessage, removeSsoErrorCodeFromSearch } from './helpers';
import styles from './styles.scss';

const { checkSso } = request;

const LOGIN_ERROR_MESSAGE = Object.freeze({
  id: 'login.error',
  defaultMessage: 'Lütfen bilgilerinizi kontrol ediniz.',
});

const Login = ({ history, location }) => {
  const [ssoErrorMessage, setSsoErrorMessage] = React.useState(() => getSsoErrorMessage(location.search));
  const [ssoUrl, setSsoUrl] = React.useState('');
  const [form, setForm] = React.useState({
    username: {
      value: '',
      error: false,
    },
    password: {
      value: '',
      error: false,
    },
  });
  const [loginError, setLoginError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (ssoErrorMessage) {
      history.replace({
        pathname: location.pathname || '/login',
        search: removeSsoErrorCodeFromSearch(location.search),
        hash: location.hash,
        state: location.state,
      });
      return;
    }

    if (Authentication.hasSession()) history.replace('/dashboard');
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadSsoConfiguration = async () => {
      try {
        const { data } = await checkSso();

        if (isActive && data?.ssoRedirect === true && data.redirectUrl) {
          setSsoUrl(data.redirectUrl);
        }
      } catch {
        if (isActive) setSsoUrl('');
      }
    };

    loadSsoConfiguration();

    return () => {
      isActive = false;
    };
  }, []);

  const handleSsoLogin = () => {
    if (ssoUrl) window.location.href = ssoUrl;
  };

  const onChange = (key, e) => {
    setForm((currentForm) => ({
      ...currentForm,
      [key]: {
        value: e.target.value,
        error: false,
      },
    }));
    setLoginError(false);
    setSsoErrorMessage(null);
  };

  const onSubmit = () => {
    const { username, password } = form;

    setSsoErrorMessage(null);

    if (!username.value || !password.value || username.value === '' || password.value === '') {
      setForm({
        username: {
          value: username.value,
          error: !username.value,
        },
        password: {
          value: password.value,
          error: !password.value,
        },
      });
      return;
    }

    setLoading(true);

    Authentication.login({
      username: form.username.value,
      password: form.password.value,
    })
      .then((data) => {
        if (data.data.redirectUrl) {
          window.open(data.data.redirectUrl, '_blank');
        }
        if (data.data.ticket) {
          history.push('/verify', { ...data.data });
        }
        if (data.data.token) {
          Authentication.setToken(data.data.token);
          localStorage.setItem('userBankDataSource', data.data?.bankDataSource);
          window.location = '/';
        }
        setLoading(false);
      })
      .catch(() => {
        setLoginError(true);
        setLoading(false);
      });
  };

  const hasFormError = form.username.error || form.password.error;
  const displayedErrorMessage = ssoErrorMessage || (hasFormError || loginError ? LOGIN_ERROR_MESSAGE : null);

  return (
    <Page>
      <Content className={styles.content}>
        <Main>
          <Widget row={3} center>
            <Card
              style={{}}
              leftItem={<div />}
              rightItem={
                <div className={styles.rightItems}>
                  <div>
                    <SelectLanguage />
                  </div>
                  <div>
                    <SelectTheme />
                  </div>
                </div>
              }>
              <Logo className={styles.logo} />
              {displayedErrorMessage && (
                <div role="alert" aria-atomic="true">
                  <Text className={cx(styles.error, { [styles['sso-error']]: Boolean(ssoErrorMessage) })}>
                    <FormattedMessage {...displayedErrorMessage} />
                  </Text>
                </div>
              )}
              <FormattedMessage id="login.username" defaultMessage="Username">
                {(msg) => {
                  return (
                    <Input
                      value={form.username.value}
                      placeholder={msg}
                      onChange={onChange.bind(this, 'username')}
                      error={form.username.error}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') return onSubmit();
                      }}
                    />
                  );
                }}
              </FormattedMessage>

              <FormattedMessage id="login.password" defaultMessage="Password">
                {(msg) => {
                  return (
                    <Input
                      value={form.password.value}
                      type="password"
                      placeholder={msg}
                      onChange={onChange.bind(this, 'password')}
                      error={form.password.error}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') return onSubmit();
                      }}
                    />
                  );
                }}
              </FormattedMessage>
              <Button
                transparent
                style={{ justifyContent: 'flex-end' }}
                onClick={() => {
                  window.location = '/forgot-password';
                }}>
                <FormattedMessage id="login.forgotPassword" defaultMessage="Şifremi unuttum" />
              </Button>
              <Button onClick={onSubmit} disabled={loading} className={styles.button}>
                {loading ? (
                  <Loader />
                ) : (
                  <Text button className={styles['button-text']}>
                    <FormattedMessage id="general.login" defaultMessage="Giriş Yap" />
                  </Text>
                )}
              </Button>
              {ssoUrl && (
                <div className={styles['sso-button']}>
                  <Button onClick={handleSsoLogin} disabled={loading}>
                    {loading ? (
                      <Loader />
                    ) : (
                      <Text button className={styles['button-text']}>
                        <FormattedMessage id="general.ssologin" defaultMessage="SSO İle Giriş Yap" />
                      </Text>
                    )}
                  </Button>
                </div>
              )}
            </Card>
          </Widget>
        </Main>
      </Content>
    </Page>
  );
};

export default Login;
